import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// common
import { updateReportFromReportListSuccess } from '../../common/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getReportReducers } from '../../report-common';
// feature fuel-cards
import * as A from './actions';
//////////////////////////////////////////////////

const initial = {
  truckList: {},
  driverList: {},
  truckGuid: null,
  filterProps: [],
  driverGuid: null,
  driverAssignmentsTrucks: {},
  showUnemployedDrivers: false,
  showIsNotInServiceTrucks: false,
};

const defaultState = {
  totalCount: 0,
  loading: false,
  itemList: null,
  usedReport: null,
  titleSortValues: {},
  reportPending: false,
  tableTitleFilters: {},
  availableReports: null,
  pagination: { limit: 20, offset: 0 },
};

const initialState = {
  ...initial,
  fullReport: defaultState,
  profileReport: defaultState,
};

const { handleUpdateReportFromReportListSuccess } = getReportReducers();

const getPrimaryObjectName = ({ truckGuid, driverGuid }) => (
  G.isOneNotNilOrNotEmpty([truckGuid, driverGuid]) ? 'profileReport' : 'fullReport'
);

const setInitialState = (state, primaryObjectName) => {
  if (R.isNil(primaryObjectName)) return initialState;

  return {
    ...state,
    [primaryObjectName]: defaultState,
  };
};

// report
const getItemListSuccess = (state, { data, guids }) => {
  const primaryObjectName = getPrimaryObjectName(state);

  const { itemList, pagination } = R.propOr({}, primaryObjectName, state);

  const { results, totalCount } = data;

  if (R.and(G.isNotNilAndNotEmpty(guids), R.equals(R.length(results), 1))) {
    const result = R.head(results);
    const guid = R.path([0, GC.FIELD_GUID], results);

    if (G.isNotNil(R.prop(guid, itemList))) {
      return P.$set(`${primaryObjectName}.itemList.${guid}`, R.mergeRight(R.prop(guid, itemList), result), state);
    }

    const remappedList = R.compose(
      R.assoc(guid, R.compose(
        R.assoc('index', 0),
        R.assoc('selected', false),
      )(result)),
      R.map((item) => R.assoc('index', R.inc(R.prop('index', item)), item)),
    )(itemList);

    return P.$all(
      P.$set(`${primaryObjectName}.itemList`, remappedList),
      P.$set(`${primaryObjectName}.totalCount`, R.inc(state.totalCount)),
      P.$set(`${primaryObjectName}.pagination.offset`, R.inc(pagination.offset)),
      state,
    );
  }

  const indexAdditional = G.ifElse(
    R.isNil(itemList),
    0,
    R.length(R.values(itemList)),
  );

  const newItems = results.map((item, index) => R.mergeRight(
    item,
    {
      selected: false,
      index: R.add(index, indexAdditional),
    },
  ));

  const list = R.mergeRight(itemList, R.indexBy(G.getGuidFromObject, newItems));
  const newOffset = R.add(pagination.offset, pagination.limit);

  return P.$all(
    P.$set(`${primaryObjectName}.itemList`, list),
    P.$set(`${primaryObjectName}.pagination.limit`, 10),
    P.$set(`${primaryObjectName}.totalCount`, totalCount),
    P.$set(
      `${primaryObjectName}.pagination.offset`,
      G.ifElse(R.gt(totalCount, newOffset), newOffset, totalCount),
    ),
    state,
  );
};

const resetListAndPagination = (state) => {
  const primaryObjectName = getPrimaryObjectName(state);

  return P.$all(
    P.$set(`${primaryObjectName}.itemList`, {}),
    P.$set(`${primaryObjectName}.totalCount`, 0),
    P.$set(`${primaryObjectName}.pagination`, defaultState.pagination),
    state,
  );
};

const selectItem = (state, id) => {
  const primaryObjectName = getPrimaryObjectName(state);

  const { itemList } = R.propOr({}, primaryObjectName, state);

  if (R.equals(id, 'all')) {
    const value = R.not(R.all(
      (item) => item.selected,
      R.values(itemList),
    ));

    return P.$set(
      `${primaryObjectName}.itemList`,
      R.map((item) => R.assoc('selected', value, item), itemList),
      state,
    );
  }

  return P.$toggle(`${primaryObjectName}.itemList.${id}.selected`, state);
};

const setUsedReport = (state, data) => {
  const primaryObjectName = getPrimaryObjectName(state);

  return P.$all(
    P.$set(`${primaryObjectName}.itemList`, {}),
    P.$set(`${primaryObjectName}.totalCount`, 0),
    P.$set(`${primaryObjectName}.usedReport`, data),
    P.$set(`${primaryObjectName}.titleSortValues`, {}),
    P.$set(`${primaryObjectName}.reportPending`, false),
    P.$set(`${primaryObjectName}.tableTitleFilters`, {}),
    P.$set(`${primaryObjectName}.globalFilterValue`, ''),
    P.$set(`${primaryObjectName}.pagination`, defaultState.pagination),
    state,
  );
};

const setReportPending = (state) => P.$set(`${getPrimaryObjectName(state)}.reportPending`, true, state);

const setListLoading = (state, loading) =>
  P.$set(`${getPrimaryObjectName(state)}.loading`, loading, state);

const setReports = (state, reports) =>
  P.$set(`${getPrimaryObjectName(state)}.availableReports`, reports, state);

const setTableTitleFilter = (state, data) => {
  const primaryObjectName = getPrimaryObjectName(state);

  const { tableTitleFilters } = R.propOr({}, primaryObjectName, state);

  const filterValues = G.getTableTitleFilterValues(tableTitleFilters, data);

  return P.$set(`${primaryObjectName}.tableTitleFilters`, filterValues, state);
};

const setTableTitleSort = (state, data) => {
  const primaryObjectName = getPrimaryObjectName(state);

  const { titleSortValues } = R.pathOr({}, [primaryObjectName], state);

  const sortValues = G.getTableTitleSortValues(titleSortValues, data);

  return P.$set(`${primaryObjectName}.titleSortValues`, sortValues, state);
};
// report

const setTruckGuid = (state, truckGuid) => P.$set('truckGuid', truckGuid, state);

const setDriverGuid = (state, driverGuid) => P.$set('driverGuid', driverGuid, state);

const getLinesByFuelCardGuidSuccess = (state, { guid, lines }) => (
  P.$set(`${getPrimaryObjectName(state)}.itemList.${guid}.lines`, lines, state)
);

const toggleFuelCardDetails = (state, { guid }) => {
  const primaryObjectName = getPrimaryObjectName(state);

  const expanded = R.not(R.path([primaryObjectName, 'itemList', guid, 'expanded'], state));

  return P.$set(`${primaryObjectName}.itemList.${guid}.expanded`, expanded, state);
};

const receivedDriverListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return P.$set('driverList', {}, state);

  const indexedDriverList = R.indexBy(R.prop(GC.FIELD_GUID), data);

  return P.$set('driverList', indexedDriverList, state);
};

const getTruckListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const indexedList = R.indexBy(R.prop(GC.FIELD_GUID), data);

  return P.$set('truckList', indexedList, state);
};

export const setDriverAssignmentsTrucks = (state, { truckGuid, driverGuid }) => (
  P.$set(`driverAssignmentsTrucks.${driverGuid}`, truckGuid, state)
);

const toggleShowUnemployedDrivers = (state) => P.$toggle('showUnemployedDrivers', state);

const toggleShowIsNotInServiceTrucks = (state) => P.$toggle('showIsNotInServiceTrucks', state);

const setAdditionalSearchCriteria = (state, data) => (
  P.$set('additionalSearchCriteria', data, state)
);

export default createReducer({
  [A.setReports]: setReports,
  [A.selectItem]: selectItem,
  [A.setTruckGuid]: setTruckGuid,
  [A.setDriverGuid]: setDriverGuid,
  [A.setUsedReport]: setUsedReport,
  [A.setListLoading]: setListLoading,
  [A.setInitialState]: setInitialState,
  [A.setReportPending]: setReportPending,
  [A.setTableTitleSort]: setTableTitleSort,
  [A.getItemListSuccess]: getItemListSuccess,
  [A.setTableTitleFilter]: setTableTitleFilter,
  [A.getTruckListSuccess]: getTruckListSuccess,
  [A.toggleFuelCardDetails]: toggleFuelCardDetails,
  [A.resetListAndPagination]: resetListAndPagination,
  [A.receivedDriverListSuccess]: receivedDriverListSuccess,
  [A.setDriverAssignmentsTrucks]: setDriverAssignmentsTrucks,
  [A.toggleShowUnemployedDrivers]: toggleShowUnemployedDrivers,
  [A.setAdditionalSearchCriteria]: setAdditionalSearchCriteria,
  [A.getLinesByFuelCardGuidSuccess]: getLinesByFuelCardGuidSuccess,
  [A.toggleShowIsNotInServiceTrucks]: toggleShowIsNotInServiceTrucks,
  [updateReportFromReportListSuccess]: handleUpdateReportFromReportListSuccess,
}, initialState);
